import React, { useEffect, useMemo, useState } from "react";
import { Page } from "./Page";
import { useQuery } from "@apollo/client/react";
import { GET_CONFIGURATION, GET_SUBSCRIPTIONS } from "../graphql/queries";
import { Error } from "./Error";
import { OrderDetails } from "../components/order/OrderDetails";
import { FormProvider, useForm } from "react-hook-form";
import { OrderFormData } from "../interfaces/OrderFormData";
import { SubscriptionLevel } from "../interfaces/SubscriptionLevel";
import { useParams } from "react-router-dom";
import { Plan, Query } from "../graphql/schema";
import { mapTitleToSubscriptionLevel } from "../helper/orderHelper";
import { OrderThankYou } from "../components/order/OrderThankYou";
import { useCmsLocale } from "../i18n/cmsLocale";

const DRAFT_KEY = "order_draft";

type OrderDraft = {
  subscription: SubscriptionLevel;
  numberOfUsers: number;
  numberOfWorkplaces: number;
};

const loadDraft = (): OrderDraft | null => {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const saveDraft = (draft: OrderDraft) => {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {}
};

const clearDraft = () => {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {}
};

export const Order = () => {
  const { selectedSubscription } = useParams();
  const locale = useCmsLocale();
  const { data, loading, error } = useQuery<Pick<Query, "subscriptions">>(
    GET_SUBSCRIPTIONS,
    { variables: { locale } },
  );
  const {
    data: configData,
    loading: configLoading,
    error: configError,
  } = useQuery<Pick<Query, "configuration">>(GET_CONFIGURATION);
  const [orderCompleted, setOrderCompleted] = useState(false);
  const [showPrices, setShowPrices] = useState(false);
  const defaultUserPrice: number = configData?.configuration?.defaultUserPrice ?? 0;
  const defaultWorkplacePrice: number = configData?.configuration?.defaultWorkplacePrice ?? 0;
  const defaultProcessPrice: number = configData?.configuration?.defaultProcessPrice ?? 0;

  useEffect(() => {
    if (configLoading || configError) {
      setShowPrices(false);
      return;
    }

    const show = configData?.configuration?.showPrices;
    setShowPrices(show ?? false);
  }, [configLoading, configError, configData]);

  const subscriptionData = useMemo(
    () => (data?.subscriptions ?? []).filter((s): s is Plan => !!s),
    [data],
  );

  const selectedId = selectedSubscription ?? null;
  const sub = useMemo(
    () =>
      (selectedId
        ? subscriptionData.find((s) => s.documentId === selectedId)
        : null) ?? null,
    [selectedId, subscriptionData],
  );

  // Load draft only when arriving without a specific subscription URL param
  const draft = !selectedSubscription ? loadDraft() : null;

  const orderForm = useForm<OrderFormData>({
    defaultValues: {
      subscription: sub
        ? mapTitleToSubscriptionLevel(sub.title ?? "")
        : (draft?.subscription ?? SubscriptionLevel.GRUNDLAGGANDE),
      products: [],
      additionalModules: [],
      baseModules: [],
      processes: [],
      numberOfUsers: sub?.users ?? draft?.numberOfUsers ?? 1,
      numberOfEmployees: sub?.users ?? draft?.numberOfUsers ?? 1,
      numberOfWorkplaces: sub?.workplaces ?? draft?.numberOfWorkplaces ?? 1,
      organisation: {
        organisationName: "",
        organisationNumber: "",
        organisationType: "",
        snis: "",
        workplaces: sub?.workplaces ?? draft?.numberOfWorkplaces ?? 1,
        employees: sub?.users ?? draft?.numberOfUsers ?? 1,
      },
      invoice: {
        invoiceName: "",
        invoiceAddress: "",
        invoiceReference: "",
        postalCode: "",
        county: "",
        phoneNumber: "",
        email: "",
        otherInvoiceDetails: "",
      },
    },
  });

  // Save subscription choice and quantities to draft whenever they change
  useEffect(() => {
    const subscription = orderForm.watch((values) => {
      if (
        values.subscription !== undefined &&
        values.numberOfUsers !== undefined &&
        values.numberOfWorkplaces !== undefined
      ) {
        saveDraft({
          subscription: values.subscription as SubscriptionLevel,
          numberOfUsers: values.numberOfUsers as number,
          numberOfWorkplaces: values.numberOfWorkplaces as number,
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [orderForm]);

  // Clear draft when order is completed
  useEffect(() => {
    if (orderCompleted) clearDraft();
  }, [orderCompleted]);

  useEffect(() => {
    if (!sub) return;

    const includedUsers = sub.users ?? 1;
    const includedWorkplaces = sub.workplaces ?? 1;

    const allowExtras =
      mapTitleToSubscriptionLevel(sub.title ?? "") !== SubscriptionLevel.STARTUP;

    orderForm.setValue(
      "subscription",
      mapTitleToSubscriptionLevel(sub.title ?? "")
    );
    orderForm.setValue("numberOfUsers", allowExtras ? includedUsers : 1);
    orderForm.setValue(
      "numberOfWorkplaces",
      allowExtras ? includedWorkplaces : 1
    );
  }, [orderForm, sub]);

  if (loading) return <></>;
  if (error)
    return (
      <Error error={error ? error.message : "Sidan hämtades inte som tänkt"} />
    );

  return (
    <Page>
      {orderCompleted ? (
        <OrderThankYou />
      ) : (
        <FormProvider {...orderForm}>
          <OrderDetails
            subscriptionData={subscriptionData}
            setOrderCompleted={setOrderCompleted}
            showPrices={showPrices}
            defaultUserPrice={defaultUserPrice}
            defaultWorkplacePrice={defaultWorkplacePrice}
            defaultProcessPrice={defaultProcessPrice}
          />
        </FormProvider>
      )}
    </Page>
  );
};
